import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const remove = () => cartItemRowToRemove.remove();

    if (prefersReducedMotion()) return remove();

    // Add class to the row to trigger the animation
    cartItemRowToRemove.style.setProperty('--row-height', `${cartItemRowToRemove.clientHeight}px`);
    cartItemRowToRemove.classList.add('removing');

    // Remove the row after the animation ends
    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        const cartError = getCartErrorMessage(parsedResponseText);
        if (cartError) {
          // stanley-gwp-validation blocks cart changes that leave a gift under the min spend.
          // Ajax responses use status/message (not always `errors`) — retry without the gift.
          return this.#retryChangeWithoutGwp(line, quantity, sectionsToUpdate, cartError);
        }

        this.#applyCartResponse(parsedResponseText);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Retries a blocked cart change by clearing GWP lines in the same update.
   * Falls back to showing the original error when there is no gift to remove.
   * @param {number} line - The line to change.
   * @param {number} quantity - The intended quantity for that line.
   * @param {Set<string>} sectionsToUpdate - The sections to re-render.
   * @param {string} originalError - The validation error from the blocked change.
   */
  async #retryChangeWithoutGwp(line, quantity, sectionsToUpdate, originalError) {
    const cart = await fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json());
    const lineItem = cart.items[line - 1];
    const gwpItems = findGwpCartItems(cart);

    if (!lineItem || gwpItems.length === 0) {
      return this.#handleCartError(line, { errors: originalError });
    }

    /** @type {Record<string, number>} */
    const updates = { [lineItem.key]: quantity };
    for (const item of gwpItems) {
      if (item.key === lineItem.key) continue;
      updates[item.key] = 0;
    }

    // Changing the gift line itself: still clear sibling gifts if any.
    if (isGwpCartItem(lineItem) && quantity > 0) {
      updates[lineItem.key] = 0;
    }

    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
    const parsedResponseText = await response.json();

    if (getCartErrorMessage(parsedResponseText)) {
      return this.#handleCartError(line, { errors: originalError });
    }

    this.#applyCartResponse(parsedResponseText);
  }

  /**
   * Dispatches the cart update and morphs the section from a cart API response.
   * @param {Object} parsedResponseText - The parsed cart API response.
   */
  #applyCartResponse(parsedResponseText) {
    const newSectionHTML = new DOMParser().parseFromString(
      parsedResponseText.sections[this.sectionId],
      'text/html'
    );

    // Grab the new cart item count from a hidden element
    const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
    const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

    this.dispatchEvent(
      new CartUpdateEvent({}, this.sectionId, {
        itemCount: newCartItemCount,
        source: 'cart-items-component',
        sections: parsedResponseText.sections,
      })
    );

    morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} [parsedResponseText.errors] - The errors.
   * @param {string} [parsedResponseText.message] - Alternate error message.
   * @param {string} [parsedResponseText.description] - Alternate error description.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = getCartErrorMessage(parsedResponseText) || '';
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}

/**
 * Normalize Ajax Cart API error payloads.
 * Built-in stock errors and cart-validation Functions may use `errors`,
 * `message`, and/or `description` with a numeric `status`.
 * @param {Record<string, unknown>} response
 * @returns {string | null}
 */
function getCartErrorMessage(response) {
  if (!response || typeof response !== 'object') return null;

  const { errors, message, description, status } = response;

  if (typeof errors === 'string' && errors.trim()) return errors;
  if (errors && typeof errors === 'object') {
    try {
      return JSON.stringify(errors);
    } catch {
      // fall through
    }
  }

  const numericStatus = typeof status === 'number' ? status : Number(status);
  if (Number.isFinite(numericStatus) && numericStatus >= 400) {
    if (typeof description === 'string' && description.trim()) return description;
    if (typeof message === 'string' && message.trim()) return message;
    return 'Cart Error';
  }

  return null;
}

/**
 * @param {{ items?: Array<Record<string, any>> }} cart
 * @returns {Array<Record<string, any>>}
 */
function findGwpCartItems(cart) {
  const configuredIds = getConfiguredGwpProductIds();
  return (cart.items || []).filter((item) => isGwpCartItem(item, configuredIds));
}

/**
 * @param {Record<string, any>} item
 * @param {Set<string>} [configuredIds]
 * @returns {boolean}
 */
function isGwpCartItem(item, configuredIds = getConfiguredGwpProductIds()) {
  if (item?.properties?._gwp != null && item.properties._gwp !== '' && item.properties._gwp !== false) {
    return true;
  }

  const productId = item?.product_id != null ? String(item.product_id) : '';
  const variantId = item?.variant_id != null ? String(item.variant_id) : '';
  return Boolean((productId && configuredIds.has(productId)) || (variantId && configuredIds.has(variantId)));
}

/**
 * Product ids from the cart progress bar / offer cards (`data-gwp-product-id`).
 * Kept on the progress bar even when the gift is already in cart (offer card hidden).
 * @returns {Set<string>}
 */
function getConfiguredGwpProductIds() {
  /** @type {Set<string>} */
  const ids = new Set();
  document.querySelectorAll('[data-gwp-product-id]').forEach((el) => {
    const id = el.getAttribute('data-gwp-product-id');
    if (id) ids.add(id);
  });
  return ids;
}
